import { api, unwrap, type APIResponse, type Paginated } from "./client";

// Mirrors app/routers/contact.py ContactGroupResponse.
export type ContactGroupStatus =
  | "uploading"
  | "processing"
  | "pending_approval"
  | "active"
  | "failed"
  | "discarded";

export interface ContactGroup {
  contact_group_id: number;
  name: string;
  description: string | null;
  contact_count: number;
  status: ContactGroupStatus;
  imported_count: number;
  dnc_removed_count: number;
  skipped_count: number;
}

// Mirrors ContactResponse.
export interface Contact {
  contact_id: number;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  mobile: string | null;
  fax: string | null;
}

export interface ContactSearchParams {
  phone?: string;
  email?: string;
  first_name?: string;
  contact_group_id?: number;
  page?: number;
  per_page?: number;
}

export async function listGroups(status?: ContactGroupStatus): Promise<ContactGroup[]> {
  const resp = await api.get<APIResponse<ContactGroup[]>>("/contacts/groups", {
    params: status ? { status } : undefined,
  });
  return unwrap(resp.data);
}

export async function createGroup(body: { name: string; description?: string }): Promise<number> {
  const resp = await api.post<APIResponse<{ contact_group_id: number }>>("/contacts/groups", body);
  return unwrap(resp.data).contact_group_id;
}

export async function deleteGroup(id: number): Promise<void> {
  await api.delete(`/contacts/groups/${id}`);
}

export async function importContacts(
  file: File,
  contactGroupId?: number,
): Promise<{ imported: number; skipped: number }> {
  const form = new FormData();
  form.append("file", file);
  const resp = await api.post<APIResponse<{ imported: number; skipped: number }>>(
    "/contacts/import",
    form,
    {
      params: contactGroupId ? { contact_group_id: contactGroupId } : undefined,
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return unwrap(resp.data);
}

export async function searchContacts(params: ContactSearchParams): Promise<Paginated<Contact>> {
  // Trailing slash: search route is at "/" (avoids the 307 redirect).
  const resp = await api.get<Paginated<Contact>>("/contacts/", { params });
  if (!resp.data.success) throw new Error(resp.data.error || "Search failed");
  return resp.data;
}

// --- Do-Not-Call list ---
// Mirrors app/routers/contact.py DNCResponse.
export interface DNCEntry {
  contact_dnc_id: number;
  phone: string | null;
  email: string | null;
  description: string | null;
  is_global: boolean;
  created_at: string | null;
}

export interface DNCListParams {
  page?: number;
  per_page?: number;
  search?: string;
}

export async function listDNC(params: DNCListParams): Promise<Paginated<DNCEntry>> {
  const resp = await api.get<Paginated<DNCEntry>>("/contacts/dnc", { params });
  if (!resp.data.success) throw new Error(resp.data.error || "Failed to load DNC list");
  return resp.data;
}

export async function addDNC(body: {
  phone?: string;
  email?: string;
  is_global?: boolean;
  description?: string;
}): Promise<void> {
  await api.post("/contacts/dnc", body);
}

export async function removeDNC(body: { phones?: string[]; emails?: string[] }): Promise<number> {
  const resp = await api.delete<APIResponse<{ removed: number }>>("/contacts/dnc", { data: body });
  return unwrap(resp.data).removed;
}

// CSV of the same rows the list shows: the caller's own entries (admins: own + global).
export async function exportDNC(search?: string): Promise<Blob> {
  const resp = await api.get<Blob>("/contacts/dnc/export", {
    params: search ? { search } : {},
    responseType: "blob",
  });
  return resp.data;
}

// --- Chunked async list upload (init → chunk* → complete → poll status → approve/discard) ---
// Why chunks: lists run to hundreds of thousands of rows, past the synchronous
// /import 10 MB cap. The browser slices the file and streams it; the server
// reassembles, parses + DNC-scrubs in the background, then waits for approval.

const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB per chunk
const PREVIEW_BYTES = 256 * 1024; // enough for header + sample rows

// Field a CSV column can map to (mirrors contact_import.TARGET_FIELDS) + ignore.
export type TargetField = "phone" | "mobile" | "first_name" | "last_name" | "email" | "fax" | "custom";

export interface ImportPreview {
  headers: string[];
  sample_rows: string[][];
  suggested: Record<string, TargetField | null>;
  target_fields: TargetField[];
}

/** Send the first slice of the file so the server detects columns + suggests a mapping. */
export async function previewImport(file: File): Promise<ImportPreview> {
  const form = new FormData();
  form.append("sample", file.slice(0, PREVIEW_BYTES), file.name);
  const resp = await api.post<APIResponse<ImportPreview>>("/contacts/import/preview", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return unwrap(resp.data);
}

export async function startListUpload(name: string, description?: string): Promise<number> {
  const resp = await api.post<APIResponse<{ contact_group_id: number }>>(
    "/contacts/import/init",
    { name, description },
  );
  return unwrap(resp.data).contact_group_id;
}

export interface UploadOptions {
  /** Confirmed {csv_header: field} mapping; omit to let the server auto-detect. */
  mapping?: Record<string, TargetField>;
  onProgress?: (fraction: number) => void;
  description?: string;
}

/**
 * Upload a CSV in chunks to an initialised list, reporting 0..1 progress, then
 * start background processing with the confirmed column mapping. Returns the
 * list (group) id.
 */
export async function uploadListChunked(
  name: string,
  file: File,
  opts: UploadOptions = {},
): Promise<number> {
  const groupId = await startListUpload(name, opts.description);
  const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  for (let index = 0; index < total; index++) {
    const blob = file.slice(index * CHUNK_SIZE, (index + 1) * CHUNK_SIZE);
    const form = new FormData();
    form.append("index", String(index));
    form.append("chunk", blob, file.name);
    await api.post<APIResponse<unknown>>(`/contacts/import/${groupId}/chunk`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    opts.onProgress?.((index + 1) / total);
  }
  await api.post<APIResponse<unknown>>(`/contacts/import/${groupId}/complete`, {
    mapping: opts.mapping ?? null,
  });
  return groupId;
}

export async function getImportStatus(groupId: number): Promise<ContactGroup> {
  const resp = await api.get<APIResponse<ContactGroup>>(`/contacts/import/${groupId}/status`);
  return unwrap(resp.data);
}

export async function approveGroup(groupId: number): Promise<void> {
  await api.post<APIResponse<unknown>>(`/contacts/groups/${groupId}/approve`, {});
}

export async function discardGroup(groupId: number): Promise<void> {
  await api.post<APIResponse<unknown>>(`/contacts/groups/${groupId}/discard`, {});
}

export interface ContactGroupResetResult {
  contact_group_id: number;
  group_name: string;
  campaigns_reset: number;
  reset: number;
  newly_added: number;
  dnc_suppressed: number;
  pending_total: number;
  campaigns: Array<{
    campaign_id: number;
    campaign_name: string;
    status: string;
    reset: number;
    newly_added: number;
    dnc_suppressed: number;
    dnc_total: number;
    pending_total: number;
    total: number;
  }>;
}

export async function resetContactGroup(groupId: number): Promise<ContactGroupResetResult> {
  const resp = await api.post<APIResponse<ContactGroupResetResult>>(
    `/contacts/groups/${groupId}/reset`,
    {},
  );
  return unwrap(resp.data);
}
